import React, { useState, useEffect, ReactNode } from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import {
  getStates,
  createState,
  updateState,
  deleteState,
  getStateAuditLog,
} from "../../../services/fees.service";
import type { StateAuditEntry } from "../../../models/stateAudit.model";
import { useAppContext } from "../../../utils/content.util";
import { onError } from "../../../utils/error.util";
import { hasPermission, Permission } from "../../../models/role.model";
import { State } from "../../../models/state.model";
import { County } from "../../../models/county.model";
import { Fee } from "../../../models/fee.model";

interface CreateFeeForm {
  stateName: string;
  stateAbbr: string;
  feeName: string;
  feeType: "fixed" | "percentage";
  feeCost: string;
  feeDescription: string;
  countyName: string;
}

function formatAuditTimestamp(ts: number): string {
  return new Date(ts).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatChangeValue(val: unknown): string {
  if (val === null || val === undefined) return "—";
  if (typeof val !== "object") return String(val);
  try {
    return JSON.stringify(val);
  } catch {
    return String(val);
  }
}

function formatFeeLine(f: Record<string, unknown>): string {
  const name = typeof f.name === "string" ? f.name : "Fee";
  const cost = f.cost != null ? String(f.cost) : "";
  if (f.type === "percentage") return `${name} (${cost}%)`;
  return `${name} ($${cost})`;
}

type JsonRecord = Record<string, unknown>;

function feesById(arr: unknown): Map<string, JsonRecord> {
  const m = new Map<string, JsonRecord>();
  if (!Array.isArray(arr)) return m;
  for (const item of arr) {
    if (item && typeof item === "object" && "id" in item) {
      m.set(String((item as JsonRecord).id), item as JsonRecord);
    }
  }
  return m;
}

function snapshotForEquality(obj: JsonRecord): string {
  const keys = Object.keys(obj).sort();
  const sorted: JsonRecord = {};
  for (const k of keys) sorted[k] = obj[k];
  try {
    return JSON.stringify(sorted);
  } catch {
    return String(obj.id);
  }
}

/** Groups audit detail so it reads as supporting content, not code. */
function AuditDiffPanel({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={(theme) => ({
        mt: 0.75,
        pl: 1.75,
        pr: 1,
        py: 1.25,
        borderLeft: `3px solid ${theme.palette.divider}`,
        borderRadius: "0 8px 8px 0",
        bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === "dark" ? 0.08 : 0.04),
      })}
    >
      {children}
    </Box>
  );
}

function AuditSectionLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="caption"
      component="div"
      sx={{
        fontWeight: 700,
        color: "text.secondary",
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        mb: 0.35,
      }}
    >
      {children}
    </Typography>
  );
}

function AuditFeeLine({ children }: { children: ReactNode }) {
  return (
    <Typography variant="body2" color="text.primary" component="div" sx={{ py: 0.125, pl: 0.5 }}>
      {children}
    </Typography>
  );
}

const auditScalarSx = {
  mt: 0.75,
  pl: 1.5,
  py: 1,
  borderLeftWidth: 3,
  borderLeftStyle: "solid",
  borderLeftColor: "divider",
  borderRadius: "0 8px 8px 0",
  whiteSpace: "pre-wrap" as const,
  wordBreak: "break-word" as const,
  fontSize: "0.875rem",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
};

type FeeAuditLabels = { added?: string; removed?: string; updated?: string };

function renderFeeListIndented(fees: unknown): ReactNode {
  if (!Array.isArray(fees) || fees.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
        No county fees yet.
      </Typography>
    );
  }
  return (
    <Box sx={{ pl: 0.5 }}>
      {(fees as unknown[]).map((f, i) =>
        f && typeof f === "object" && "name" in f ? (
          <AuditFeeLine key={i}>• {formatFeeLine(f as JsonRecord)}</AuditFeeLine>
        ) : (
          <AuditFeeLine key={i}>• {formatChangeValue(f)}</AuditFeeLine>
        ),
      )}
    </Box>
  );
}

function renderFeesAuditDiff(from: unknown, to: unknown, labels?: FeeAuditLabels): ReactNode {
  const L = {
    added: labels?.added ?? "Added",
    removed: labels?.removed ?? "Removed",
    updated: labels?.updated ?? "Updated",
  };
  const fromMap = feesById(from);
  const toMap = feesById(to);
  const added: JsonRecord[] = [];
  const removed: JsonRecord[] = [];
  const changed: { before: JsonRecord; after: JsonRecord }[] = [];

  for (const [id, after] of toMap) {
    const before = fromMap.get(id);
    if (!before) added.push(after);
    else if (snapshotForEquality(before) !== snapshotForEquality(after)) {
      changed.push({ before, after });
    }
  }
  for (const [id, before] of fromMap) {
    if (!toMap.has(id)) removed.push(before);
  }

  if (added.length === 0 && removed.length === 0 && changed.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
        No fee changes in this update.
      </Typography>
    );
  }

  return (
    <Box>
      {removed.length > 0 ? (
        <Box sx={{ mb: added.length || changed.length ? 1.25 : 0 }}>
          <AuditSectionLabel>{L.removed}</AuditSectionLabel>
          {removed.map((f) => (
            <AuditFeeLine key={String(f.id)}>• {formatFeeLine(f)}</AuditFeeLine>
          ))}
        </Box>
      ) : null}
      {added.length > 0 ? (
        <Box sx={{ mb: changed.length ? 1.25 : 0 }}>
          <AuditSectionLabel>{L.added}</AuditSectionLabel>
          {added.map((f) => (
            <AuditFeeLine key={String(f.id)}>• {formatFeeLine(f)}</AuditFeeLine>
          ))}
        </Box>
      ) : null}
      {changed.length > 0 ? (
        <Box>
          <AuditSectionLabel>{L.updated}</AuditSectionLabel>
          {changed.map(({ before, after }) => (
            <AuditFeeLine key={String(after.id)}>
              • {formatFeeLine(before)} → {formatFeeLine(after)}
            </AuditFeeLine>
          ))}
        </Box>
      ) : null}
    </Box>
  );
}

function countiesById(arr: unknown): Map<string, JsonRecord> {
  const m = new Map<string, JsonRecord>();
  if (!Array.isArray(arr)) return m;
  for (const item of arr) {
    if (item && typeof item === "object" && "id" in item) {
      m.set(String((item as JsonRecord).id), item as JsonRecord);
    }
  }
  return m;
}

function countySnapshot(c: JsonRecord): string {
  const name = typeof c.name === "string" ? c.name : "";
  const fees = feesById(c.fees);
  const feeParts = [...fees.keys()].sort().map((id) => `${id}:${snapshotForEquality(fees.get(id)!)}`);
  return `${name}|${feeParts.join(";")}`;
}

function renderCountiesAuditDiff(from: unknown, to: unknown): ReactNode {
  const fromM = countiesById(from);
  const toM = countiesById(to);
  const added: JsonRecord[] = [];
  const removed: JsonRecord[] = [];
  const changed: { before: JsonRecord; after: JsonRecord }[] = [];

  for (const [id, after] of toM) {
    const before = fromM.get(id);
    if (!before) added.push(after);
    else if (countySnapshot(before) !== countySnapshot(after)) {
      changed.push({ before, after });
    }
  }
  for (const [id, before] of fromM) {
    if (!toM.has(id)) removed.push(before);
  }

  if (added.length === 0 && removed.length === 0 && changed.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 0.25 }}>
        No county changes in this update.
      </Typography>
    );
  }

  const countyTitle = (c: JsonRecord) =>
    typeof c.name === "string" && c.name.length > 0 ? c.name : `County ${String(c.id)}`;

  const nestedFeeLabels: FeeAuditLabels = {
    added: "New fees",
    removed: "Removed fees",
    updated: "Changed fees",
  };

  return (
    <Box>
      {removed.length > 0 ? (
        <Box sx={{ mb: added.length || changed.length ? 1.25 : 0 }}>
          <AuditSectionLabel>Removed counties</AuditSectionLabel>
          {removed.map((c) => (
            <AuditFeeLine key={String(c.id)}>• {countyTitle(c)}</AuditFeeLine>
          ))}
        </Box>
      ) : null}
      {added.length > 0 ? (
        <Box sx={{ mb: changed.length ? 1.25 : 0 }}>
          <AuditSectionLabel>New counties</AuditSectionLabel>
          {added.map((c) => (
            <Box key={String(c.id)} sx={{ mb: 1 }}>
              <Typography variant="subtitle2" sx={{ pl: 0.5, mb: 0.25, fontWeight: 600 }}>
                {countyTitle(c)}
              </Typography>
              {renderFeeListIndented(c.fees)}
            </Box>
          ))}
        </Box>
      ) : null}
      {changed.length > 0 ? (
        <Box>
          <AuditSectionLabel>Updated counties</AuditSectionLabel>
          {changed.map(({ before, after }) => {
            const nameBefore = typeof before.name === "string" ? before.name : "";
            const nameAfter = typeof after.name === "string" ? after.name : "";
            const nameChanged = nameBefore !== nameAfter;
            return (
              <Box
                key={String(after.id)}
                sx={(theme) => ({
                  mb: 1.25,
                  pl: 1,
                  ml: 0.25,
                  borderLeft: `2px solid ${alpha(theme.palette.divider, 0.9)}`,
                })}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
                  {countyTitle(after)}
                </Typography>
                {nameChanged ? (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 0.5, mb: 0.75 }}>
                    Renamed: {nameBefore || "—"} → {nameAfter || "—"}
                  </Typography>
                ) : null}
                <Box sx={{ pl: 0.25 }}>
                  {renderFeesAuditDiff(before.fees, after.fees, nestedFeeLabels)}
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : null}
    </Box>
  );
}

function auditEntrySummary(entry: StateAuditEntry): string {
  const who = entry.actorDisplayName || "Someone";
  const when = formatAuditTimestamp(entry.timestamp);
  switch (entry.action) {
    case "created":
      return `Fee configuration was created by ${who} on ${when}`;
    case "deleted":
      return `${who} deleted this fee configuration on ${when}`;
    case "updated":
      if (entry.changedFields && entry.changedFields.length > 0) {
        const fields = entry.changedFields.join(", ");
        return `${who} updated ${fields} on ${when}`;
      }
      return `${who} updated this fee configuration on ${when}`;
    default:
      return `${who} — ${when}`;
  }
}

function auditEntryChanges(entry: StateAuditEntry): ReactNode {
  if (entry.action !== "updated" || !entry.changes || Object.keys(entry.changes).length === 0)
    return null;
  return (
    <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
      {Object.entries(entry.changes).map(([field, { from, to }]) => (
        <li key={field}>
          <Typography variant="body2" component="div" color="text.secondary" sx={{ mb: 0.75 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              {field}
            </Box>
            {field === "fees" ? (
              <AuditDiffPanel>{renderFeesAuditDiff(from, to)}</AuditDiffPanel>
            ) : field === "counties" ? (
              <AuditDiffPanel>{renderCountiesAuditDiff(from, to)}</AuditDiffPanel>
            ) : (
              <Box sx={auditScalarSx}>
                From: {formatChangeValue(from)}
                {"\n"}
                To: {formatChangeValue(to)}
              </Box>
            )}
          </Typography>
        </li>
      ))}
    </Box>
  );
}

// Wizard steps
const steps = ["Statewide Fees", "Counties", "County Fees", "Review & Save"];

export default function FeesContainer() {
  const { currentUser } = useAppContext();
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feeActivityTab, setFeeActivityTab] = useState(0);
  const [auditLog, setAuditLog] = useState<StateAuditEntry[]>([]);
  const [auditLogLoading, setAuditLogLoading] = useState(false);

  // Load states from API on component mount
  useEffect(() => {
    const loadStates = async () => {
      try {
        setLoading(true);
        const statesData = await getStates();
        setStates(statesData);
        setError(null);
      } catch (err) {
        console.error("Error loading states:", err);
        setError("Failed to load states. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadStates();
  }, []);

  const [createStateForm, setCreateStateForm] = useState<CreateFeeForm>({
    stateName: "",
    stateAbbr: "",
    feeName: "",
    feeType: "fixed",
    feeCost: "",
    feeDescription: "",
    countyName: "",
  });

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [isWizardMode, setIsWizardMode] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Dialog states
  const [showAddStateDialog, setShowAddStateDialog] = useState(false);
  const [showAddFeeDialog, setShowAddFeeDialog] = useState(false);
  const [showAddCountyDialog, setShowAddCountyDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    type: "state" | "county" | "fee";
    id: string;
    name: string;
  } | null>(null);

  // Editing states
  const [editingFee, setEditingFee] = useState<string | null>(null);
  const [editingCountyFee, setEditingCountyFee] = useState<{
    stateId: string;
    countyId: string;
    feeId: string;
  } | null>(null);

  // UI state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showActivityTabWithPermission = Boolean(
    isWizardMode &&
      selectedState?.id &&
      currentUser &&
      hasPermission(currentUser, Permission.VIEW_FEE_LOGS),
  );

  useEffect(() => {
    if (!showActivityTabWithPermission || !selectedState?.id || feeActivityTab !== 1) return;
    let cancelled = false;
    setAuditLogLoading(true);
    getStateAuditLog(selectedState.id, { limit: 50 })
      .then((res) => {
        if (!cancelled) setAuditLog(res.items);
      })
      .catch((e) => {
        if (!cancelled) onError(e);
      })
      .finally(() => {
        if (!cancelled) setAuditLogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedState?.id, showActivityTabWithPermission, feeActivityTab]);

  const handleCreateStateChange =
    (field: keyof CreateFeeForm) =>
    (
      event:
        | React.ChangeEvent<HTMLInputElement>
        | { target: { value: unknown } }
    ) => {
      setCreateStateForm({
        ...createStateForm,
        [field]: event.target.value,
      });
    };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSaveState = async () => {
    if (!selectedState) return;

    try {
      // Update the state in the backend with all the embedded data
      const updatedState = await updateState(selectedState.id, {
        name: selectedState.name,
        abbreviation: selectedState.abbreviation,
        fees: selectedState.fees,
        counties: selectedState.counties,
      });

      // Update the local states array with the saved data
      setStates(
        states.map((state) =>
          state.id === selectedState.id ? updatedState : state
        )
      );

      // Update the selected state with the saved data
      setSelectedState(updatedState);

      showSnackbar("State configuration saved successfully!", "success");

      // Reset the wizard
      handleReset();
    } catch (err) {
      console.error("Error saving state:", err);
      showSnackbar(
        "Failed to save state configuration. Please try again.",
        "error"
      );
    }
  };

  const handleReset = () => {
    setActiveStep(0);
    setFeeActivityTab(0);
    setSelectedState(null);
    setIsWizardMode(false);
    setIsEditMode(false);
    setCreateStateForm({
      stateName: "",
      stateAbbr: "",
      feeName: "",
      feeType: "fixed",
      feeCost: "",
      feeDescription: "",
      countyName: "",
    });
  };

  // const startWizard = () => {
  //   setIsWizardMode(true);
  //   setActiveStep(0);
  // };

  const editState = (state: State) => {
    setSelectedState(state);
    setFeeActivityTab(0);
    setIsEditMode(true);
    setIsWizardMode(true);
    setActiveStep(0); // Start at the beginning
  };

  const handleAddState = async () => {
    if (createStateForm.stateName && createStateForm.stateAbbr) {
      try {
        const newState = await createState({
          name: createStateForm.stateName,
          abbreviation: createStateForm.stateAbbr,
        });
        setStates([...states, newState]);
        setSelectedState(newState);
        setCreateStateForm({
          stateName: "",
          stateAbbr: "",
          feeName: "",
          feeType: "fixed",
          feeCost: "",
          feeDescription: "",
          countyName: "",
        });
        setShowAddStateDialog(false);
        handleNext();
        showSnackbar("State added successfully!", "success");
      } catch (err) {
        console.error("Error creating state:", err);
        showSnackbar("Failed to create state. Please try again.", "error");
      }
    }
  };

  const handleAddFee = async (stateId: string, countyId?: string) => {
    if (createStateForm.feeName && createStateForm.feeCost) {
      try {
        // Create the fee object locally
        const newFee: Fee = {
          id: `fee_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: createStateForm.feeName,
          type: createStateForm.feeType,
          cost: createStateForm.feeCost,
          description: createStateForm.feeDescription,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Update the local state to include the new fee
        const updatedStates = states.map((state) => {
          if (state.id === stateId) {
            if (countyId) {
              // Add fee to specific county
              return {
                ...state,
                counties: state.counties.map((county) =>
                  county.id === countyId
                    ? { ...county, fees: [...county.fees, newFee] }
                    : county
                ),
              };
            } else {
              // Add fee to state
              return {
                ...state,
                fees: [...state.fees, newFee],
              };
            }
          }
          return state;
        });

        setStates(updatedStates);

        // Update selected state if we're in edit mode
        if (selectedState && selectedState.id === stateId) {
          const updatedState = updatedStates.find((s) => s.id === stateId);
          if (updatedState) {
            setSelectedState(updatedState);
          }
        }

        setCreateStateForm({
          ...createStateForm,
          feeName: "",
          feeType: "fixed",
          feeCost: "",
          feeDescription: "",
        });
        setShowAddFeeDialog(false);
        setEditingCountyFee(null);
        showSnackbar("Fee added successfully!", "success");
      } catch (err) {
        console.error("Error creating fee:", err);
        showSnackbar("Failed to create fee. Please try again.", "error");
      }
    }
  };

  const handleAddCounty = async (stateId: string) => {
    if (createStateForm.countyName) {
      try {
        // Create the county object locally
        const newCounty: County = {
          id: `county_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: createStateForm.countyName,
          fees: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        // Update the local state to include the new county
        const updatedStates = states.map((state) => {
          if (state.id === stateId) {
            return {
              ...state,
              counties: [...(state.counties || []), newCounty],
            };
          }
          return state;
        });

        setStates(updatedStates);

        // Update selected state if we're in edit mode
        if (selectedState && selectedState.id === stateId) {
          const updatedState = updatedStates.find((s) => s.id === stateId);
          if (updatedState) {
            setSelectedState(updatedState);
          }
        }

        setCreateStateForm({
          ...createStateForm,
          countyName: "",
        });
        setShowAddCountyDialog(false);
        showSnackbar("County added successfully!", "success");
      } catch (err) {
        console.error("Error creating county:", err);
        showSnackbar("Failed to create county. Please try again.", "error");
      }
    }
  };

  const handleEditFee = (stateId: string, feeId: string, countyId?: string) => {
    const state = states.find((s) => s.id === stateId);
    let fee: Fee | undefined;

    if (countyId) {
      const county = state?.counties.find((c) => c.id === countyId);
      fee = county?.fees.find((f) => f.id === feeId);
    } else {
      fee = state?.fees.find((f) => f.id === feeId);
    }

    if (fee) {
      setCreateStateForm({
        ...createStateForm,
        feeName: fee.name,
        feeType: fee.type,
        feeCost: fee.cost,
        feeDescription: fee.description || "",
      });

      // Clear previous editing states
      setEditingFee(null);
      setEditingCountyFee(null);

      if (countyId) {
        // Editing a county fee
        setEditingCountyFee({ stateId, countyId, feeId });
      } else {
        // Editing a state fee
        setEditingFee(feeId);
      }
      setShowAddFeeDialog(true);
    }
  };

  const handleSaveFee = (stateId: string, countyId?: string) => {
    if (createStateForm.feeName && createStateForm.feeCost) {
      const updatedStates = states.map((state) => {
        if (state.id === stateId) {
          if (countyId && editingCountyFee) {
            return {
              ...state,
              counties: state.counties.map((county) =>
                county.id === countyId
                  ? {
                      ...county,
                      fees: county.fees.map((fee) =>
                        fee.id === editingCountyFee.feeId
                          ? {
                              ...fee,
                              name: createStateForm.feeName,
                              type: createStateForm.feeType,
                              cost: createStateForm.feeCost,
                              description: createStateForm.feeDescription,
                              updatedAt: new Date().toISOString(),
                            }
                          : fee
                      ),
                    }
                  : county
              ),
            };
          } else if (editingFee) {
            return {
              ...state,
              fees: state.fees.map((fee) =>
                fee.id === editingFee
                  ? {
                      ...fee,
                      name: createStateForm.feeName,
                      type: createStateForm.feeType,
                      cost: createStateForm.feeCost,
                      description: createStateForm.feeDescription,
                      updatedAt: new Date().toISOString(),
                    }
                  : fee
              ),
            };
          }
        }
        return state;
      });

      setStates(updatedStates);

      // Update selected state if we're in edit mode
      if (selectedState && selectedState.id === stateId) {
        const updatedState = updatedStates.find((s) => s.id === stateId);
        if (updatedState) {
          setSelectedState(updatedState);
        }
      }

      setCreateStateForm({
        ...createStateForm,
        feeName: "",
        feeType: "fixed",
        feeCost: "",
        feeDescription: "",
      });
      setEditingFee(null);
      setEditingCountyFee(null);
      setShowAddFeeDialog(false);
      showSnackbar("Fee updated successfully!", "success");
    }
  };

  const handleDelete = (
    type: "state" | "county" | "fee",
    id: string,
    name: string
  ) => {
    setDeleteTarget({ type, id, name });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const { type, id } = deleteTarget;

    try {
      if (type === "state") {
        await deleteState(id);
        setStates(states.filter((state) => state.id !== id));
        if (selectedState?.id === id) {
          setSelectedState(null);
        }
        showSnackbar("State deleted successfully!", "success");
      } else if (type === "county") {
        // Find the state that contains this county
        const state = states.find((s) => s.counties?.some((c) => c.id === id));
        if (state) {
          // Remove county from local state
          const updatedStates = states.map((s) =>
            s.id === state.id
              ? { ...s, counties: s.counties?.filter((c) => c.id !== id) || [] }
              : s
          );
          setStates(updatedStates);

          // Update selected state if we're in edit mode
          if (selectedState?.id === state.id) {
            const updatedState = updatedStates.find((s) => s.id === state.id);
            if (updatedState) {
              setSelectedState(updatedState);
            }
          }
        }
        showSnackbar("County deleted successfully!", "success");
      } else if (type === "fee") {
        // Find the state and county that contains this fee
        const state = states.find(
          (s) =>
            s.fees?.some((f) => f.id === id) ||
            s.counties?.some((c) => c.fees?.some((f) => f.id === id))
        );
        if (state) {
          const county = state.counties?.find((c) =>
            c.fees?.some((f) => f.id === id)
          );
          let updatedStates;

          if (county) {
            // Remove fee from county
            updatedStates = states.map((s) =>
              s.id === state.id
                ? {
                    ...s,
                    counties: s.counties?.map((c) =>
                      c.id === county.id
                        ? {
                            ...c,
                            fees: c.fees?.filter((f) => f.id !== id) || [],
                          }
                        : c
                    ),
                  }
                : s
            );
          } else {
            // Remove fee from state
            updatedStates = states.map((s) =>
              s.id === state.id
                ? { ...s, fees: s.fees?.filter((f) => f.id !== id) || [] }
                : s
            );
          }

          setStates(updatedStates);

          // Update selected state if we're in edit mode
          if (selectedState?.id === state.id) {
            const updatedState = updatedStates.find((s) => s.id === state.id);
            if (updatedState) {
              setSelectedState(updatedState);
            }
          }
        }
        showSnackbar("Fee deleted successfully!", "success");
      }

      setShowDeleteDialog(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error("Error deleting item:", err);
      showSnackbar("Failed to delete item. Please try again.", "error");
    }
  };

  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const renderFeeCard = (fee: Fee, stateId: string, countyId?: string) => (
    <Card key={fee.id} sx={{ mb: 2, boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
      <CardContent sx={{ pb: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Typography
            variant="h6"
            component="h3"
            sx={{ fontWeight: 600, fontSize: "1rem" }}
          >
            {fee.name}
          </Typography>
          <Chip
            label={fee.type === "percentage" ? `${fee.cost}%` : `$${fee.cost}`}
            color={fee.type === "percentage" ? "primary" : "secondary"}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </Box>
        {fee.description && (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mb: 1, fontStyle: "normal" }}
          >
            {fee.description}
          </Typography>
        )}
      </CardContent>
      <CardActions sx={{ pt: 0, justifyContent: "flex-end", px: 2, pb: 1 }}>
        <IconButton
          size="small"
          onClick={() => handleEditFee(stateId, fee.id, countyId)}
          sx={{
            color: "#333",
            "&:hover": { backgroundColor: "rgba(51, 51, 51, 0.08)" },
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => handleDelete("fee", fee.id, fee.name)}
          sx={{
            color: "#333",
            "&:hover": { backgroundColor: "rgba(51, 51, 51, 0.08)" },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );

  // Wizard Step Components
  const renderStepContent = (step: number) => {
    switch (step) {
      case -1:
        return (
          <Box sx={{ p: 3 }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{ fontWeight: 600, color: "#0a2543" }}
            >
              Select a State to Configure
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 3, color: "text.secondary", fontStyle: "normal" }}
            >
              Choose an existing state to edit, or create a new one to get
              started.
            </Typography>

            <Grid container spacing={2}>
              {states.map((state) => (
                <Grid key={state.id} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      "&:hover": { boxShadow: 3 },
                      border: "1px solid #e0e0e0",
                    }}
                    onClick={() => {
                      setSelectedState(state);
                      handleNext();
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: 600, color: "#0a2543" }}
                      >
                        {state.abbreviation} - {state.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontStyle: "normal" }}
                      >
                        {state.fees?.length || 0} state fees •{" "}
                        {state.counties?.length || 0} counties
                      </Typography>
                    </CardContent>
                    <CardActions>
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        sx={{ fontWeight: 500 }}
                      >
                        Edit
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}

              {/* <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    border: '2px dashed',
                    borderColor: 'divider',
                    '&:hover': { borderColor: 'primary.main' },
                  }}
                  onClick={() => setShowAddStateDialog(true)}
                >
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    <AddIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Add New State
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'normal' }}>
                      Create a new state configuration
                    </Typography>
                  </CardContent>
                </Card>
              </Grid> */}
            </Grid>
          </Box>
        );

      case 0:
        return (
          <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setShowAddFeeDialog(true);
                    setEditingFee(null);
                    setEditingCountyFee(null);
                  }}
                  sx={{ fontWeight: 500 }}
                >
                  Add Fee
                </Button>
              </Box>

              {(selectedState?.fees?.length || 0) === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: "text.secondary",
                    fontStyle: "normal",
                  }}
                >
                  No state fees configured yet. Add your first fee to get
                  started.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {(selectedState?.fees || []).map((fee) => (
                    <Grid key={fee.id} size={{ xs: 12, sm: 6, md: 4 }}>
                      {renderFeeCard(fee, selectedState?.id || "")}
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ p: 3 }}>
            <h3>{selectedState?.abbreviation} Counties</h3>

            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowAddCountyDialog(true)}
                  sx={{ fontWeight: 500 }}
                >
                  Add County
                </Button>
              </Box>

              {(selectedState?.counties?.length || 0) === 0 ? (
                <Typography
                  variant="body2"
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: "text.secondary",
                    fontStyle: "normal",
                  }}
                >
                  No counties added yet. Add counties that have specific fee
                  requirements.
                </Typography>
              ) : (
                <List>
                  {(selectedState?.counties || []).map((county) => (
                    <ListItem
                      key={county.id}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1,
                      }}
                    >
                      <ListItemText
                        primary={county.name}
                        secondary={`${
                          county.fees?.length || 0
                        } fees configured`}
                        slotProps={{
                          primary: { sx: { fontWeight: 500 } },
                          secondary: { sx: { fontStyle: 'normal' } },
                        }}
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          onClick={() =>
                            handleDelete("county", county.id, county.name)
                          }
                          sx={{
                            color: "#333",
                            "&:hover": {
                              backgroundColor: "rgba(51, 51, 51, 0.08)",
                            },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ p: 3 }}>
            <h3>County Fees</h3>
            <Typography
              variant="body1"
              sx={{ mb: 3, color: "text.secondary", fontStyle: "normal" }}
            >
              Set up fees for each county in {selectedState?.abbreviation}.
            </Typography>

            {(selectedState?.counties || []).map((county) => (
              <Box key={county.id} sx={{ mb: 4 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: "secondary.main", fontWeight: 600 }}
                  >
                    {county.name} County
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => {
                      setShowAddFeeDialog(true);
                      setEditingFee(null);
                      setEditingCountyFee({
                        stateId: selectedState?.id || "",
                        countyId: county.id,
                        feeId: "",
                      });
                      // Reset form for new fee
                      setCreateStateForm({
                        ...createStateForm,
                        feeName: "",
                        feeType: "fixed",
                        feeCost: "",
                        feeDescription: "",
                      });
                    }}
                    sx={{ fontWeight: 500 }}
                  >
                    Add County Fee
                  </Button>
                </Box>

                {(county.fees?.length || 0) === 0 ? (
                  <Typography
                    variant="body2"
                    sx={{
                      textAlign: "center",
                      py: 2,
                      color: "text.secondary",
                      fontStyle: "normal",
                    }}
                  >
                    No fees configured for this county yet.
                  </Typography>
                ) : (
                  <Grid container spacing={2}>
                    {(county.fees || []).map((fee) => (
                      <Grid key={fee.id} size={{ xs: 12, sm: 6, md: 4 }}>
                        {renderFeeCard(fee, selectedState?.id || "", county.id)}
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            ))}
          </Box>
        );

      case 3:
        return (
          <Box sx={{ p: 3 }}>
            <h3>Review & Save</h3>
            <Typography
              variant="body1"
              sx={{ mb: 3, color: "text.secondary", fontStyle: "normal" }}
            >
              Review your fee configuration for {selectedState?.abbreviation}{" "}
              before saving.
            </Typography>

            <Card sx={{ mb: 3, border: "1px solid #e0e0e0" }}>
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontWeight: 600, color: "#0a2543" }}
                >
                  {selectedState?.abbreviation} - {selectedState?.name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontStyle: "normal" }}
                >
                  {selectedState?.fees?.length || 0} state fees •{" "}
                  {selectedState?.counties?.length || 0} counties
                </Typography>
              </CardContent>
            </Card>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              {/* <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Total Configuration
              </Typography> */}
              <Chip
                label="Ready to Save"
                color="success"
                icon={<CheckIcon />}
                sx={{ fontWeight: 500 }}
              />
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  // Main Dashboard View
  const renderDashboard = () => (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ p: 3 }}>
        {/* Header Section - Matching Service Forms style */}
        <div className="applications">
          <h2 className="pb-2 mt-4 mb-3 border-bottom">Fee Management</h2>
        </div>

        {/* Loading and Error States */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && (
          <>
            {/* Quick Actions Toolbar */}
            <div className="pb-2 mt-4 mb-3">
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  gap: 1,
                }}
              >
                <Button
                  variant="contained"
                  onClick={() => setShowAddStateDialog(true)}
                >
                  New State
                </Button>
              </Box>
            </div>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>State</TableCell>
                    <TableCell>State Name</TableCell>
                    <TableCell align="center">Fees</TableCell>
                    <TableCell align="center">Counties</TableCell>
                    <TableCell> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {states.map((state) => (
                    <TableRow key={state.id}>
                      <TableCell>{state.abbreviation}</TableCell>
                      <TableCell>
                        <a
                          href="#"
                          onClick={(e) => {
                            e.preventDefault();
                            editState(state);
                          }}
                        >
                          <span>{state.name}</span>
                        </a>
                      </TableCell>
                      <TableCell align="center">
                        {state.fees?.length || 0}
                      </TableCell>
                      <TableCell align="center">
                        {state.counties?.length || 0}
                      </TableCell>
                      <TableCell align="left">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Tooltip title="Delete">
                            <ReactRouterLink
                              onClick={() =>
                                handleDelete("state", state.id, state.name)
                              }
                              to=""
                            >
                              <DeleteIcon color="error" fontSize="small" />
                            </ReactRouterLink>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>
    </Box>
  );

  // Wizard View
  const renderWizard = () => (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ p: 3, boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <IconButton onClick={handleReset} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <h2>{isEditMode ? "Edit State Configuration" : "Configure Fees"}</h2>
        </Box>

        {showActivityTabWithPermission ? (
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={feeActivityTab}
              onChange={(_e, v) => setFeeActivityTab(v)}
              aria-label="fee configuration tabs"
            >
              <Tab label="Configuration" id="fee-tab-0" />
              <Tab label="Activity" id="fee-tab-1" />
            </Tabs>
          </Box>
        ) : null}

        <Box
          sx={{
            display:
              showActivityTabWithPermission && feeActivityTab === 1 ? "none" : "block",
          }}
        >
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel sx={{ fontWeight: 500 }}>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              sx={{ fontWeight: 500 }}
            >
              Back
            </Button>
            <Box>
              <Button onClick={handleReset} sx={{ mr: 1, fontWeight: 500 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={
                  activeStep === steps.length - 1 ? handleSaveState : handleNext
                }
                endIcon={
                  activeStep === steps.length - 1 ? (
                    <CheckIcon />
                  ) : (
                    <ArrowForwardIcon />
                  )
                }
                sx={{ fontWeight: 500 }}
              >
                {activeStep === steps.length - 1 ? "Finish" : "Next"}
              </Button>
            </Box>
          </Box>

          {renderStepContent(activeStep)}
        </Box>

        {showActivityTabWithPermission && feeActivityTab === 1 ? (
          <Box sx={{ pt: 1 }}>
            <Typography variant="h6" gutterBottom>
              Audit log
            </Typography>
            {auditLogLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : auditLog.length === 0 ? (
              <Typography color="text.secondary">No activity yet.</Typography>
            ) : (
              <List dense disablePadding>
                {auditLog.map((entry, idx) => (
                  <ListItem
                    key={`${entry.timestamp}-${idx}`}
                    alignItems="flex-start"
                    disableGutters
                    sx={{ flexDirection: "column", alignItems: "stretch" }}
                  >
                    <ListItemText
                      primary={auditEntrySummary(entry)}
                      secondary={auditEntryChanges(entry)}
                      slotProps={{
                        primary: { variant: 'body2' },
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        ) : null}
      </Paper>
    </Box>
  );

  return (
    <>
      {isWizardMode ? renderWizard() : renderDashboard()}

      {/* Add State Dialog */}
      <Dialog
        open={showAddStateDialog}
        onClose={() => setShowAddStateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#0a2543" }}>
          Add New State
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="State Name"
                value={createStateForm.stateName}
                onChange={handleCreateStateChange("stateName")}
                required
                sx={{ "& .MuiInputLabel-root": { fontWeight: 500 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="State Abbreviation"
                value={createStateForm.stateAbbr}
                onChange={handleCreateStateChange("stateAbbr")}
                required
                slotProps={{ htmlInput: { maxLength: 2 } }}
                sx={{ "& .MuiInputLabel-root": { fontWeight: 500 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAddStateDialog(false)}
            sx={{ fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddState}
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            Add State
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Fee Dialog */}
      <Dialog
        open={showAddFeeDialog}
        onClose={() => setShowAddFeeDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#0a2543" }}>
          {editingFee
            ? "Edit Fee"
            : editingCountyFee
            ? "Add County Fee"
            : "Add State Fee"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Fee Name"
                value={createStateForm.feeName}
                onChange={handleCreateStateChange("feeName")}
                required
                sx={{ "& .MuiInputLabel-root": { fontWeight: 500 } }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontWeight: 500 }}>Fee Type</InputLabel>
                <Select
                  value={createStateForm.feeType}
                  label="Fee Type"
                  onChange={(e) => handleCreateStateChange("feeType")(e as any)}
                >
                  <MenuItem value="fixed">Fixed Amount ($)</MenuItem>
                  <MenuItem value="percentage">Percentage (%)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Cost"
                value={createStateForm.feeCost}
                onChange={handleCreateStateChange("feeCost")}
                required
                type="number"
                slotProps={{
                  htmlInput: {
                    step: createStateForm.feeType === "percentage" ? 0.01 : 0.01,
                    min: 0,
                  },
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        {createStateForm.feeType === "percentage" ? "%" : "$"}
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{ "& .MuiInputLabel-root": { fontWeight: 500 } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Description (Optional)"
                value={createStateForm.feeDescription}
                onChange={handleCreateStateChange("feeDescription")}
                multiline
                rows={3}
                sx={{ "& .MuiInputLabel-root": { fontWeight: 500 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAddFeeDialog(false)}
            sx={{ fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (editingFee) {
                handleSaveFee(selectedState?.id || "");
              } else if (editingCountyFee) {
                // For county fees, check if it's a new fee (empty feeId) or editing existing
                if (editingCountyFee.feeId === "") {
                  handleAddFee(
                    editingCountyFee.stateId,
                    editingCountyFee.countyId
                  );
                } else {
                  handleSaveFee(
                    editingCountyFee.stateId,
                    editingCountyFee.countyId
                  );
                }
              } else {
                handleAddFee(selectedState?.id || "");
              }
            }}
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            {editingFee ? "Save Changes" : "Add Fee"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add County Dialog */}
      <Dialog
        open={showAddCountyDialog}
        onClose={() => setShowAddCountyDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#0a2543" }}>
          Add New County
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="County Name"
            value={createStateForm.countyName}
            onChange={handleCreateStateChange("countyName")}
            required
            sx={{ mt: 1, "& .MuiInputLabel-root": { fontWeight: 500 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowAddCountyDialog(false)}
            sx={{ fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleAddCounty(selectedState?.id || "")}
            variant="contained"
            sx={{ fontWeight: 500 }}
          >
            Add County
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#0a2543" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontStyle: "normal" }}>
            Are you sure you want to delete "{deleteTarget?.name}"? This action
            cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowDeleteDialog(false)}
            sx={{ fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            sx={{
              backgroundColor: "#333",
              fontWeight: 500,
              "&:hover": { backgroundColor: "#555" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
